import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import AccessDenied from "components/AccessDenied";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import RuvixxDate from "components/RuvixxDate";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import { formatError } from "helpers/formatError";
import MetaDatasetService from "services/MetaDataset";
import { MetaDatasetTableActions } from "routes/meta_datasets/MetaDatasetTableActions";

export const STANDALONE_DATASETS_QUERY_KEY = ["meta-standalone-datasets"];

const buildColumns = ({ canManage, onEdit, onDelete }) => [
  {
    header: "ID",
    id: "ID",
    accessorKey: "id",
    size: 50,
    cell: ({ row }) => (
      <p style={{ fontWeight: "bold" }}>{row.getValue("ID")}</p>
    ),
  },
  { id: "Name", accessorKey: "name", header: "Name", size: 200 },
  {
    id: "Dataset ID",
    accessorKey: "dataset_id",
    header: "Dataset ID",
    size: 200,
  },
  {
    id: "Created",
    accessorKey: "created_at",
    header: "Created",
    size: 180,
    cell: ({ row }) => {
      const date = row.getValue("Created");
      return date ? <RuvixxDate date={date} /> : null;
    },
  },
  {
    header: null,
    id: "Actions",
    accessorKey: "actions",
    size: 60,
    enableSorting: false,
    enableResizing: false,
    meta: { isDisplay: true },
    cell: ({ row }) => (
      <MetaDatasetTableActions
        dataset={row.original}
        canManage={canManage}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    ),
  },
];

const MetaDatasetsPage = () => {
  const navigate = useNavigate();
  const canManage = useAuthorized([
    ACL_RELATIONSHIPS.metaAdAccount.manageAccounts,
  ]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  usePageTitle("Meta datasets");

  const invalidateDatasets = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: STANDALONE_DATASETS_QUERY_KEY,
      }),
    []
  );

  const handleEdit = useCallback(
    dataset => navigate(`/settings/meta_datasets/${dataset.id}/edit`),
    [navigate]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        canManage,
        onEdit: handleEdit,
        onDelete: setDeleteTarget,
      }),
    [canManage, handleEdit]
  );

  const fetchDatasets = useCallback(
    async queryParams => {
      const next = getNextQueryParams(queryParams, columns);
      return MetaDatasetService.listStandalone(next);
    },
    [columns]
  );

  const handleDelete = useCallback(async () => {
    try {
      await MetaDatasetService.deleteStandalone(deleteTarget.id);
      toast.success("Dataset deleted");
      setDeleteTarget(null);
      invalidateDatasets();
    } catch (error) {
      toast.error(formatError(error, "Could not delete dataset"));
    }
  }, [deleteTarget, invalidateDatasets]);

  if (!canManage) {
    return (
      <AccessDenied description="You don't have permission to manage Meta datasets." />
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:min-w-0">
      <BaseTableV3
        columns={columns}
        header="Meta datasets"
        tableName="meta_standalone_datasets"
        queryKey={STANDALONE_DATASETS_QUERY_KEY}
        fetchData={fetchDatasets}
        invalidateData={invalidateDatasets}
        enableSearch
        enableSettings
        createButton={
          <Button
            size="sm"
            onClick={() => navigate("/settings/meta_datasets/new")}
          >
            New Dataset
          </Button>
        }
      />
      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={open => !open && setDeleteTarget(null)}
        title="Delete dataset"
        description={`Delete ${deleteTarget?.name}?`}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default MetaDatasetsPage;
